import React, { FC, useState } from 'react';
import { Button, Drawer, Form, Input, InputNumber, Select } from 'antd';
import { useForm, useWatch } from 'antd/es/form/Form';
import { SuccessCard } from 'common/components';
import { EFormFieldMessage } from 'common/const';
import { objectIsEmpty, rules } from 'common/helpers';
import { useInventoryContext } from 'common/context';
import { CHARACTER_LIMIT_MIN, DEFAULT_VALUE_0 } from 'common/config';
import { IFormValue } from 'common/models';
import { ArrowDownShort, ArrowLeftLong, Close } from 'common/svg';
import { UserHooks } from 'common/hooks';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { InventoryAvailableCount } from 'entities/Inventory/components';
import { EInventoryActionType } from 'entities/Inventory/Inventory.enum';
import { getWriteOffReasonOptions, inventoryCount } from 'entities/Inventory/Inventory.helper';
import { GoodsCardMin } from 'entities/Goods/components';
import { filterInventoryListByCount, selectInventory } from 'entities/Inventory/Inventory.slice';
import { createInventoryAction, selectIsInventoryActionLoading } from 'entities/InventoryAction/InventoryAction.slice';
import { getUserInventoryStats } from 'entities/User/User.slice';

export const InventoryWriteOffCard: FC = () => {
  const dispatch = useAppDispatch();
  // inventory
  const inventory = useAppSelector(selectInventory);
  // inventoryAction
  const isInventoryActionLoading = useAppSelector(selectIsInventoryActionLoading);

  const [openSuccessCard, setOpenSuccessCard] = useState<boolean>(false);
  const [count, setCount] = useState<number | null>(null);
  const [form] = useForm();
  const formValues = useWatch([], form);
  const { isNestedCard, setIsNestedCard, openInventoryWriteOffCard, setOpenInventoryWriteOffCard } = useInventoryContext();
  const { isAccountAdmin } = UserHooks.useCurrentUserRole();

  const availableCount = inventoryCount.availableCount(inventory);
  const submitDisabled = formValues && (objectIsEmpty(formValues) || !formValues.reason || !formValues.count);

  const onClose = () => {
    if (isInventoryActionLoading) {
      return;
    }

    setOpenInventoryWriteOffCard(false);

    setIsNestedCard(false);
    form.resetFields();
  };

  const onSuccessCardClose = () => {
    setOpenSuccessCard(false);
    form.resetFields();
  };

  const onSubmit = (values: IFormValue) => {
    const { reason, count, comment } = values;

    if (inventory) {
      dispatch(
        createInventoryAction({
          inventoryId: inventory.id,
          type: EInventoryActionType.WriteOff,
          count,
          reason,
          comment,
          onSuccess: () => {
            setOpenInventoryWriteOffCard(false);
            setOpenSuccessCard(true);
            dispatch(filterInventoryListByCount());
            dispatch(getUserInventoryStats({}));
          },
        }),
      );
    }
  };

  if (!inventory) {
    return null;
  }

  return (
    <>
      <Drawer
        rootClassName={`redesign drawer ${isNestedCard ? 'nested' : ''}`}
        open={openInventoryWriteOffCard}
        onClose={onClose}
        width={800}
        destroyOnClose
        closeIcon={isNestedCard ? <ArrowLeftLong /> : <Close />}
        classNames={{ footer: 'flex align-center justify-between' }}
        footer={[
          <Button key="cancel" className="button-lg secondary" onClick={onClose} loading={isInventoryActionLoading}>
            Отмена
          </Button>,
          <Button
            key="writeOff"
            className="button-lg primary"
            loading={isInventoryActionLoading}
            onClick={form.submit}
            disabled={submitDisabled}
          >
            Направить запрос
          </Button>,
        ]}
      >
        <div className="drawer__body">
          <div className="ff-regular fs-xxl mb-14">Списание оборудования</div>

          <div className="mb-6">
            <GoodsCardMin good={inventory?.good} />
          </div>

          {inventory?.count && (
            <div className="mb-9">
              <InventoryAvailableCount availableCount={availableCount} totalCount={inventory.count} />
            </div>
          )}

          <Form layout="vertical" requiredMark={false} form={form} onFinish={onSubmit}>
            <Form.Item className="mb-9" label="Причина*" name="reason" rules={[rules.required()]}>
              <Select
                rootClassName="redesign"
                options={getWriteOffReasonOptions()}
                placeholder="Выберите причину списания"
                virtual={false}
                suffixIcon={<ArrowDownShort />}
              />
            </Form.Item>

            <Form.Item
              className="mb-9"
              label="Количество оборудования*"
              name="count"
              rules={[
                rules.required(),
                () => ({
                  validator(_, value) {
                    if (value > availableCount || value <= DEFAULT_VALUE_0) {
                      return Promise.reject(new Error('Невозможно указать введённое количество.'));
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber controls={false} suffix="шт" onChange={(value) => setCount(value as number)} />
            </Form.Item>

            <Form.Item
              className="mb-9"
              label="Комментарий"
              name="comment"
              rules={[rules.min(CHARACTER_LIMIT_MIN, EFormFieldMessage.CharacterLimitMin)]}
            >
              <Input.TextArea rows={4} className="textarea" placeholder="Укажите дополнительные сведения о причине списания" />
            </Form.Item>
          </Form>
        </div>
      </Drawer>

      <SuccessCard
        open={openSuccessCard}
        content={
          isAccountAdmin
            ? `Оборудование в количестве ${count} шт списано с баланса подразделения`
            : 'Запрос о списании оборудования направлен на согласование'
        }
        subtitle="Готово!"
        btnTitle="Назад к списку"
        onClose={onSuccessCardClose}
        onConfirm={onSuccessCardClose}
      />
    </>
  );
};
